import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import './cookie.css';

// Reusable animated component
function AnimatedSection({ children, className }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
    >
      {children}
    </motion.div>
  );
}

function CookiePolicy() {
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Cookie categories data
  const cookieCategories = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      description: 'These cookies are necessary for the website to function and cannot be switched off in our systems.',
      necessary: true,
      cookies: [
        { name: 'session_id', purpose: 'Maintain user session state', duration: 'Session' },
        { name: 'csrf_token', purpose: 'Security protection against CSRF attacks', duration: 'Session' },
        { name: 'cookie_consent', purpose: 'Store your cookie preferences', duration: '1 year' }
      ]
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      description: 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site.',
      necessary: false,
      cookies: [
        { name: '_ga', purpose: 'Google Analytics - Distinguish unique users', duration: '2 years' },
        { name: '_gid', purpose: 'Google Analytics - Distinguish unique users', duration: '24 hours' },
        { name: '_gat', purpose: 'Google Analytics - Throttle request rate', duration: '1 minute' }
      ]
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      description: 'These cookies enable the website to provide enhanced functionality and personalization.',
      necessary: false,
      cookies: [
        { name: 'language_pref', purpose: 'Store language preference', duration: '1 year' },
        { name: 'theme_pref', purpose: 'Store theme preference (light/dark)', duration: '1 year' }
      ]
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'These cookies may be set through our site by our advertising partners to build a profile of your interests.',
      necessary: false,
      cookies: [
        { name: '_fbp', purpose: 'Facebook Pixel - Store and track visits', duration: '3 months' },
        { name: 'li_fat_id', purpose: 'LinkedIn Insight Tag - Store and track visits', duration: '1 month' }
      ]
    }
  ];

  // Last updated date
  const lastUpdated = new Date('2024-01-15').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="cookie-policy-container">
      {/* ===== HEADER SECTION ===== */}
      <section className="cookie-hero">
        <div className="hero-background">
          <div className="hero-pattern"></div>
        </div>
        <motion.div 
          className="cookie-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="hero-badge">
            <span>Privacy & Compliance</span>
          </div>
          <h1>Cookie <span className="gradient-text">Policy</span></h1>
          <p className="hero-subtitle">
            Learn how LEARNEX uses cookies and similar technologies to enhance your experience, 
            analyze site usage, and support our marketing efforts.
          </p>
          <div className="last-updated">
            Last updated: {lastUpdated}
          </div>
        </motion.div>
      </section>

      {/* ===== QUICK SUMMARY ===== */}
      <AnimatedSection className="cookie-summary">
        <div className="summary-cards">
          <motion.div 
            className="summary-card"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="summary-icon">📋</div>
            <h3>Transparent</h3>
            <p>Clear information about all cookies we use</p>
          </motion.div>
          
          <motion.div 
            className="summary-card"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="summary-icon">⚙️</div>
            <h3>Controllable</h3>
            <p>Manage your preferences at any time</p>
          </motion.div>
          
          <motion.div 
            className="summary-card"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="summary-icon">🛡️</div>
            <h3>Compliant</h3>
            <p>Meets GDPR, CCPA, and other regulations</p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* ===== WHAT ARE COOKIES ===== */}
      <AnimatedSection className="what-are-cookies">
        <div className="section-header">
          <h2>What Are Cookies?</h2>
          <p>Understanding how cookies work and why they're used</p>
        </div>
        
        <div className="cookies-explanation">
          <div className="explanation-content">
            <p>
              Cookies are small text files that are stored on your device when you visit a website. 
              They are widely used to make websites work more efficiently and provide information to the site owners.
            </p>
            
            <div className="cookie-facts">
              <div className="fact">
                <h4>📝 Text Files</h4>
                <p>Cookies are simple text files, not programs or software</p>
              </div>
              <div className="fact">
                <h4>🔒 Secure</h4>
                <p>They cannot access other information on your device</p>
              </div>
              <div className="fact">
                <h4>⏰ Temporary</h4>
                <p>Most cookies are automatically deleted after a period of time</p>
              </div>
            </div>
          </div>
          
          <div className="explanation-visual">
            <div className="cookie-diagram">
              <div className="browser">Browser</div>
              <div className="arrow">→</div>
              <div className="server">Server</div>
              <div className="cookie-icon">🍪</div>
              <div className="data-flow">Data Exchange</div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== COOKIE CATEGORIES ===== */}
      <AnimatedSection className="cookie-categories">
        <div className="section-header">
          <h2>Types of Cookies We Use</h2>
          <p>Detailed information about each category of cookies</p>
        </div>
        
        <div className="category-tabs">
          {cookieCategories.map(category => (
            <button
              key={category.id}
              className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.name}
              {category.necessary && <span className="necessary-badge">Required</span>}
            </button>
          ))}
        </div>
        
        <div className="category-content">
          {cookieCategories
            .filter(category => activeCategory === 'all' || category.id === activeCategory)
            .map(category => (
              <motion.div
                key={category.id}
                className="category-details"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <h3>{category.name}</h3>
                <p>{category.description}</p>
                
                <div className="cookies-table">
                  <div className="table-header">
                    <span>Cookie Name</span>
                    <span>Purpose</span>
                    <span>Duration</span>
                  </div>
                  {category.cookies.map((cookie, index) => (
                    <div key={index} className="table-row">
                      <span className="cookie-name">{cookie.name}</span>
                      <span className="cookie-purpose">{cookie.purpose}</span>
                      <span className="cookie-duration">{cookie.duration}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
        </div>
      </AnimatedSection>

      {/* ===== COOKIE MANAGEMENT ===== */}
      <AnimatedSection className="cookie-management">
        <div className="section-header">
          <h2>Managing Your Preferences</h2>
          <p>How to control and manage cookie settings</p>
        </div>
        
        <div className="management-options">
          <div className="management-card">
            <div className="management-icon">🌐</div>
            <h3>Browser Settings</h3>
            <p>
              Most web browsers allow you to control cookies through their settings preferences. 
              However, limiting cookies may affect your experience on our website.
            </p>
            <ul>
              <li>Google Chrome: Settings → Privacy and security → Cookies and other site data</li>
              <li>Mozilla Firefox: Options → Privacy & Security → Cookies and Site Data</li>
              <li>Safari: Preferences → Privacy → Cookies and website data</li>
              <li>Microsoft Edge: Settings → Privacy, search, and services → Cookies</li>
            </ul>
          </div>
          
          <div className="management-card">
            <div className="management-icon">⚙️</div>
            <h3>Our Cookie Consent Tool</h3>
            <p>
              Use our cookie consent manager to customize your preferences. You can update your 
              choices at any time by clicking the "Cookie Settings" link in the website footer.
            </p>
            <div className="consent-actions">
              <motion.button 
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Update Cookie Preferences
              </motion.button>
            </div>
          </div>
          
          <div className="management-card">
            <div className="management-icon">📧</div>
            <h3>Contact Us</h3>
            <p>
              If you have any questions about our use of cookies or need assistance with managing 
              your preferences, please contact our Data Protection Officer.
            </p>
            <div className="contact-info">
              <p>Email: <a href="mailto:privacy@learnex.lk">privacy@learnex.lk</a></p>
              <p>Phone: +94 70 1111 735</p>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== LEGAL COMPLIANCE ===== */}
      <AnimatedSection className="legal-compliance">
        <div className="section-header">
          <h2>Legal Compliance</h2>
          <p>Our commitment to privacy regulations</p>
        </div>
        
        <div className="compliance-content">
          <div className="compliance-text">
            <p>
              LEARNEX is committed to complying with global privacy regulations including:
            </p>
            
            <div className="regulation-list">
              <div className="regulation">
                <h4>GDPR</h4>
                <p>General Data Protection Regulation (EU)</p>
              </div>
              <div className="regulation">
                <h4>CCPA</h4>
                <p>California Consumer Privacy Act</p>
              </div>
              <div className="regulation">
                <h4>PDPA</h4>
                <p>Personal Data Protection Act (Sri Lanka)</p>
              </div>
            </div>
            
            <p>
              We regularly review and update our cookie practices to ensure ongoing compliance 
              with evolving regulations and best practices.
            </p>
          </div>
          
          <div className="compliance-badge">
            <div className="badge-icon">🛡️</div>
            <span>Privacy Compliant</span>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== FREQUENTLY ASKED QUESTIONS ===== */}
      <AnimatedSection className="cookie-faq">
        <div className="section-header">
          <h2>Frequently Asked Questions</h2>
          <p>Common questions about our cookie policy</p>
        </div>
        
        <div className="faq-list">
          <div className="faq-item">
            <h3>Do I have to accept cookies?</h3>
            <p>
              While you can choose to reject non-essential cookies, please note that essential cookies 
              are required for the website to function properly. Rejecting certain cookies may limit 
              your experience on our site.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>How often is this policy updated?</h3>
            <p>
              We review our cookie policy annually or whenever there are significant changes to our 
              practices or applicable regulations. The "Last updated" date at the top of this page 
              indicates when the policy was last revised.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>Do you use third-party cookies?</h3>
            <p>
              Yes, we use some third-party cookies for analytics and marketing purposes. These are 
              clearly identified in our cookie categories table, and you can control your preferences 
              for these cookies through our consent management tool.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>How can I delete cookies?</h3>
            <p>
              You can delete cookies through your browser settings. The process varies by browser, 
              but typically you can find this option in the privacy or history section of your 
              browser's settings menu.
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== CONTACT SECTION ===== */}
      <AnimatedSection className="cookie-contact">
        <div className="contact-content">
          <h2>Still Have Questions?</h2>
          <p>Contact our privacy team for more information about our cookie practices</p>
          <div className="contact-actions">
            <motion.a 
              href="mailto:privacy@learnex.lk"
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Email Our Privacy Team
            </motion.a>
            <motion.a 
              href="/contact"
              className="btn btn-secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              General Contact Form
            </motion.a>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
}

export default CookiePolicy;